/**
 * Timeline Editor Data Model
 *
 * TypeScript interfaces for the timeline editing state.
 * Supports video and audio clips with trimming, reordering, speed control, and playback.
 */

/**
 * Represents a video clip on the timeline
 */
export interface TimelineClip {
  /** Unique clip ID (uuid) */
  id: string

  /** Upstream VideoGeneratorNode ID */
  sourceNodeId: string

  /** Path to video file (local_path) */
  sourcePath: string

  /** Full duration of source clip (seconds) */
  originalDuration: number

  /** Trim offset from start (seconds, default 0) */
  trimStart: number

  /** Trim offset from end (seconds, default 0) */
  trimEnd: number

  /** Computed: (originalDuration - trimStart - trimEnd) / speed */
  effectiveDuration: number

  /** Position in sequence (0-indexed) */
  order: number

  /** Offset from timeline start (seconds, default: legacy cumulative position) */
  startOffset: number

  /** Video track index; 0 is topmost and wins overlap preview/render */
  trackIndex: number

  /** Display name (filename or custom) */
  label: string

  /** Playback speed multiplier (default 1.0) */
  speed: number

  /** Volume level (0.0 to 1.0, default 1.0) */
  volume: number

  /** Mute state (default false) */
  muted: boolean

  /**
   * Fade-out duration in seconds (default 0 / undefined = no fade).
   * When > 0, the last `fadeOutS` seconds of the clip fade to black on the
   * rendered video, and the timeline shows a tapered overlay on the right
   * edge of the clip. See RFC-0501.
   */
  fadeOutS?: number

  /**
   * Fade-in duration in seconds (default 0 / undefined = no fade).
   * Symmetric to fadeOutS: the first `fadeInS` seconds ramp from black to
   * the video. Both fades are rendered live in VideoPreviewPlayer.
   */
  fadeInS?: number
}

/**
 * Represents an audio clip on the timeline
 */
export interface TimelineAudioClip {
  /** Unique clip ID (uuid) */
  id: string

  /** Upstream AudioNode ID */
  sourceNodeId: string

  /** Path to audio file (local_path) */
  sourcePath: string

  /** Full duration of source clip (seconds) */
  originalDuration: number

  /** Trim offset from start (seconds, default 0) */
  trimStart: number

  /** Trim offset from end (seconds, default 0) */
  trimEnd: number

  /** Computed: (originalDuration - trimStart - trimEnd) / speed */
  effectiveDuration: number

  /** Volume level (0.0 to 1.0) */
  volume: number

  /** Mute state */
  muted: boolean

  /** Offset from timeline start (seconds, default 0) */
  startOffset: number

  /** Audio role for multi-track mixing */
  role: 'original' | 'replacement' | 'sfx'

  /** Playback speed multiplier (default 1.0) */
  speed: number

  /** Track index for multi-track layout (default 0) */
  trackIndex: number
}

/**
 * Main timeline state containing all editing data
 */
export interface TimelineState {
  /** Video clips in order */
  clips: TimelineClip[]

  /** Audio track clips */
  audioClips: TimelineAudioClip[]

  /** Current time in seconds */
  playheadPosition: number

  /** Pixels per second (for timeline scale) */
  zoomLevel: number

  /** Playback state */
  isPlaying: boolean

  /**
   * IDs of all currently selected clips (video AND audio).
   * Empty set means nothing selected. Single-element set is the common case;
   * use shift-click to extend / toggle the selection.
   */
  selectedClipIds: Set<string>

  /** Computed from clips: total duration of all clips in sequence */
  totalDuration: number

  /** Number of visible video tracks */
  videoTrackCount: number

  /** Number of visible audio tracks */
  audioTrackCount: number

  /** Clip stored in clipboard for copy/paste (null if none) */
  copiedClip: TimelineClip | TimelineAudioClip | null

  /** Type of the copied clip (null if none) */
  copiedClipType: 'video' | 'audio' | null

  /**
   * Set of muted video track indices (RFC-0651). A track index in this set
   * silences the embedded audio of every clip on that video track during
   * preview playback. Persisted via YDoc on the videoEditor node.
   */
  mutedVideoTracks: Set<number>

  /**
   * Set of muted audio track indices (RFC-0651). A track index in this set
   * silences every audio clip on that track during preview playback.
   */
  mutedAudioTracks: Set<number>

  /**
   * "Main Track Magnet" toggle (RFC-0651, default true). When true, edits
   * to track-0 video clips re-pack the track left-to-right with no gaps on
   * commit (release of trim / move / split / extend / multi-drag).
   */
  magnetEnabled: boolean
}

/**
 * Timeline track (extensible for future multi-track support)
 */
export interface TimelineTrack {
  /** Unique track ID */
  id: string

  /** Track type */
  type: 'video' | 'audio'

  /** Clips on this track */
  clips: (TimelineClip | TimelineAudioClip)[]

  /** Lock state (prevent editing) */
  locked: boolean

  /** Mute state (disable audio playback) */
  muted: boolean
}

/**
 * Undo/Redo history entry
 */
export interface TimelineHistoryEntry {
  /** Snapshot of timeline state */
  state: TimelineState

  /** Description of the action (for debugging) */
  description: string

  /** Timestamp of the action */
  timestamp: number
}

/**
 * Action types for timeline operations
 */
export type TimelineAction =
  | { type: 'ADD_CLIP'; clip: TimelineClip }
  | { type: 'REMOVE_CLIP'; clipId: string }
  | { type: 'REORDER_CLIP'; clipId: string; newOrder: number }
  | { type: 'SPLIT_CLIP'; clipId: string; splitTime: number }
  | { type: 'TRIM_CLIP'; clipId: string; trimStart: number; trimEnd: number; startOffset?: number }
  | { type: 'TRIM_CLIP_PREVIEW'; clipId: string; trimStart: number; trimEnd: number; startOffset?: number }
  | { type: 'SET_CLIP_OFFSET'; clipId: string; startOffset: number }
  | { type: 'SET_CLIP_TRACK_INDEX'; clipId: string; trackIndex: number }
  | { type: 'SELECT_CLIP'; clipId: string | null; clipType?: 'video' | 'audio' | null }
  | { type: 'TOGGLE_CLIP_SELECTION'; clipId: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'RIPPLE_DELETE_SELECTED' }
  | { type: 'MULTI_DRAG_OFFSET'; deltaSec: number; trackDelta?: number }
  | { type: 'SET_PLAYHEAD'; position: number }
  | { type: 'SET_ZOOM'; zoomLevel: number }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'ADD_AUDIO_CLIP'; clip: TimelineAudioClip }
  | { type: 'ADD_AUDIO_CLIPS'; clips: TimelineAudioClip[] }
  | { type: 'REMOVE_AUDIO_CLIP'; clipId: string }
  | { type: 'SET_AUDIO_VOLUME'; clipId: string; volume: number }
  | { type: 'TOGGLE_AUDIO_MUTE'; clipId: string }
  | { type: 'SET_AUDIO_OFFSET'; clipId: string; startOffset: number }
  | { type: 'SET_AUDIO_TRACK_INDEX'; clipId: string; trackIndex: number }
  | { type: 'TRIM_AUDIO_CLIP'; clipId: string; trimStart: number; trimEnd: number; startOffset?: number }
  | { type: 'TRIM_AUDIO_CLIP_PREVIEW'; clipId: string; trimStart: number; trimEnd: number; startOffset?: number }
  | { type: 'SET_CLIP_SPEED'; clipId: string; speed: number }
  | { type: 'SET_CLIP_VOLUME'; clipId: string; volume: number }
  | { type: 'SET_CLIP_FADE_OUT'; clipId: string; fadeOutS: number }
  | { type: 'SET_CLIP_FADE_IN'; clipId: string; fadeInS: number }
  | { type: 'NORMALIZE_TIMELINE_ORIGIN' }
  | { type: 'CLOSE_ALL_GAPS' }
  | { type: 'TOGGLE_CLIP_MUTE'; clipId: string }
  | { type: 'SET_AUDIO_SPEED'; clipId: string; speed: number }
  | { type: 'SPLIT_AUDIO_CLIP'; clipId: string; splitTime: number }
  | { type: 'REORDER_AUDIO_CLIP'; clipId: string; newOrder: number }
  | { type: 'COPY_CLIP' }
  | { type: 'PASTE_CLIP' }
  | { type: 'DELETE_SELECTED_CLIP' }
  | { type: 'SPLIT_SELECTED_CLIP' }
  | { type: 'ADD_VIDEO_TRACK' }
  | { type: 'ADD_AUDIO_TRACK' }
  | { type: 'REMOVE_VIDEO_TRACK'; trackIndex: number; deleteClips: boolean }
  | { type: 'REMOVE_AUDIO_TRACK'; trackIndex: number; deleteClips: boolean }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESTORE_STATE'; state: TimelineState }
  | { type: 'TOGGLE_TRACK_MUTE'; trackKind: 'video' | 'audio'; trackIndex: number }
  | { type: 'SET_MAGNET'; enabled: boolean }
  | { type: 'MAGNET_PACK_TRACK0' }
  | { type: 'EXTRACT_AUDIO_FROM_CLIP'; sourceClipId: string; audioClip: TimelineAudioClip; newAudioTrackIndex: number }

/**
 * Drag operation state
 */
export interface DragState {
  /** Dragging active */
  isDragging: boolean

  /** ID of clip being dragged */
  draggedClipId: string | null

  /** Type of drag operation */
  dragType: 'reorder' | 'trim-start' | 'trim-end' | null

  /** Starting position (pixels) */
  startX: number

  /** Current position (pixels) */
  currentX: number

  /** Snap target position (pixels, null if no snap) */
  snapPosition: number | null
}

/** Render configuration sent to backend. Generated from schemas/nodes/videoEditor.json. */
export type TimelineRenderConfig = TimelineConfigDict
import type { TimelineConfigDict } from '@ponpon/api-client'

import type { ReactFlowFieldValue } from '../../../../hooks/useYCanvas'

export function isTimelineRenderConfig(v: ReactFlowFieldValue | undefined): v is TimelineRenderConfig {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return false
  if (!('operation' in v) || !('timeline' in v)) return false
  return (v.operation === 'timeline_render')
}
